"use client";

import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/analytics";

type Suggestion = { id: string; text: string };

/**
 * Multiline site-address field with UK search-as-you-type (Google Places via
 * the worker's /api/address — matches building/business names, streets and
 * postcodes). Degrades to a plain textarea when the lookup is unavailable.
 */
export function AddressField({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  className: string;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [available, setAvailable] = useState(true);
  const session = useRef<string>("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setSuggestions([]);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function handleInput(next: string) {
    onChange(next);
    if (!available) return;
    if (timer.current) clearTimeout(timer.current);
    // Search on the first line only — once a full address is in place the
    // remaining lines are postcode/town and re-searching is noise.
    const term = next.split("\n")[0].trim();
    if (term.length < 3 || next.includes("\n")) {
      setSuggestions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        if (!session.current) session.current = crypto.randomUUID();
        const res = await fetch("/api/address", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ op: "suggest", input: term, session: session.current }),
        });
        if (res.status === 503) {
          setAvailable(false);
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch {
        /* lookup is best-effort */
      }
    }, 250);
  }

  async function pick(s: Suggestion) {
    setSuggestions([]);
    try {
      const res = await fetch("/api/address", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ op: "details", placeId: s.id, session: session.current }),
      });
      session.current = "";
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      onChange(data.address || s.text);
      track("address_lookup_used", {});
    } catch {
      onChange(s.text);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <textarea
        rows={3}
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={available ? "Start typing a site name, street or postcode…" : undefined}
        className={className}
        aria-autocomplete="list"
        aria-expanded={suggestions.length > 0}
      />
      {suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-line bg-white shadow-float" role="listbox">
          {suggestions.map((s) => (
            <li key={s.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => pick(s)}
                className="w-full px-3 py-2 text-left text-[0.8rem] leading-snug text-body hover:bg-emerald-tint hover:text-ink"
              >
                {s.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
